export type LegFocus = "left" | "right" | "both";

export type ConfidenceBand = "green" | "yellow" | "red";

type ConfidenceMeans = {
  meanLeftConfidence?: number | null;
  meanRightConfidence?: number | null;
};

type JointLike = {
  name?: string;
  side?: string;
};

export const getExamLegFocus = (joint?: JointLike | null): LegFocus | null => {
  if (!joint) {
    return null;
  }
  if (joint.name === "bothKnees") {
    return "both";
  }
  if (joint.side === "left" || joint.side === "right") {
    return joint.side;
  }
  return null;
};

const isValidMean = (value?: number | null): value is number =>
  typeof value === "number" && !Number.isNaN(value);

const roundTo1Decimal = (value: number): number => Math.round(value * 10) / 10;

export const getTrackingConfidenceScore = (
  means: ConfidenceMeans | null | undefined,
  focus: LegFocus | null,
): number | null => {
  if (!means || !focus) {
    return null;
  }

  const { meanLeftConfidence, meanRightConfidence } = means;

  if (focus === "left") {
    return isValidMean(meanLeftConfidence)
      ? roundTo1Decimal(meanLeftConfidence * 10)
      : null;
  }

  if (focus === "right") {
    return isValidMean(meanRightConfidence)
      ? roundTo1Decimal(meanRightConfidence * 10)
      : null;
  }

  // focus === "both": require both means; incomplete data → null
  if (!isValidMean(meanLeftConfidence) || !isValidMean(meanRightConfidence)) {
    return null;
  }

  return roundTo1Decimal(
    ((meanLeftConfidence + meanRightConfidence) / 2) * 10,
  );
};

export const getConfidenceBand = (
  score: number | null,
): ConfidenceBand | null => {
  if (score === null) {
    return null;
  }
  if (score >= 7) {
    return "green";
  }
  if (score >= 4) {
    return "yellow";
  }
  return "red";
};
